import { AlertCircle } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { FindingCard } from '@/features/hypotheses'
import type { HypothesisRun } from '@/types/hypothesis'
import type { HypothesisFinding } from '@/types/hypothesis-finding'
import { Section } from './Section'

export function FindingsList({
  findings,
  latestRun,
  caseId,
  highlightIndex,
}: {
  findings: HypothesisFinding[] | undefined
  latestRun: HypothesisRun | undefined
  caseId: string
  /** 0-based index of the finding to scroll to and flash — set when an [F#]
   * citation in the report is clicked. */
  highlightIndex?: number | null
}) {
  const { t } = useTranslation()
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])

  // Scroll the cited finding into view when a report [F#] chip targets it.
  useEffect(() => {
    if (highlightIndex == null) return
    itemRefs.current[highlightIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [highlightIndex])

  const hasFindings = findings && findings.length > 0
  const isEmpty = latestRun?.status === 'finished_success' && findings && findings.length === 0
  if (!hasFindings && !isEmpty) return null

  return (
    <Section
      title={t('search.detail.findings.title')}
      hint={
        hasFindings
          ? t('search.detail.findings.hint', { count: findings.length })
          : t('search.detail.findings.emptyHint')
      }
    >
      {hasFindings ? (
        <div className="flex flex-col gap-4">
          {findings.map((f, i) => (
            <div
              key={f.id}
              ref={el => {
                itemRefs.current[i] = el
              }}
            >
              <FindingCard
                finding={f}
                caseId={caseId}
                index={i}
                highlighted={highlightIndex === i}
              />
            </div>
          ))}
        </div>
      ) : (
        <Card size="compact" tone="warning" className="flex flex-row items-start gap-3 p-4">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-warning" />
          <div className="flex flex-col gap-1.5 text-sm">
            <p className="font-medium text-foreground">{t('search.detail.findings.emptyTitle')}</p>
            <p className="text-muted-foreground">{t('search.detail.findings.emptyBody')}</p>
          </div>
        </Card>
      )}
    </Section>
  )
}
